export type CorrelatedChartType = 'demand' | 'temperature' | 'door'

export type CorrelatedChartPair = 'base' | 'compare'

/** Espaço reservado à esquerda para rótulos numéricos (ex.: "-30 C") nos line charts. */
export const CORRELATED_Y_AXIS_LABEL_WIDTH = 64

/**
 * Gutter esquerda da plot area no rangeBar de porta (yaxis oculto).
 * Um pouco menor que a soma label+padding dos line charts — o Apex ainda reserva
 * folga residual no rangeBar e empurrava a timeline para a direita.
 */
export const CORRELATED_DOOR_PLOT_LEFT_PADDING = 52

/**
 * Padding extra do grid. O alinhamento da plot area entre line e rangeBar depende
 * sobretudo de `CORRELATED_Y_AXIS_LABEL_WIDTH` + gutter da porta.
 */
export const CORRELATED_CHART_GRID_PADDING = {
  left: 8,
  right: 16,
  top: 0,
  bottom: 0,
} as const

export interface ICorrelatedChartsPresent {
  demand: boolean
  temperature: boolean
  door: boolean
}

export function buildCorrelatedXAxisOptions(params: {
  min?: number
  max?: number
  showLabels: boolean
}) {
  return {
    min: params.min,
    max: params.max,
    type: 'datetime' as const,
    labels: {
      show: params.showLabels,
      rotate: 0,
      format: 'dd/MM/yyyy HH:mm',
      hideOverlappingLabels: true,
      datetimeUTC: false,
    },
  }
}

const CHART_STACK_ORDER: CorrelatedChartType[] = ['demand', 'temperature', 'door']

export function resolveBottomChartType(present: ICorrelatedChartsPresent): CorrelatedChartType | null {
  for (let index = CHART_STACK_ORDER.length - 1; index >= 0; index -= 1) {
    const chartType = CHART_STACK_ORDER[index]
    if (present[chartType]) return chartType
  }
  return null
}

export function countCorrelatedCharts(present: ICorrelatedChartsPresent): number {
  return CHART_STACK_ORDER.filter((type) => present[type]).length
}

export interface ICorrelatedChartLayoutParams {
  showXAxisLabels: boolean
  xAxisMin?: number
  xAxisMax?: number
  /** RangeBar de porta: esconde labels de categoria (nome fica no tooltip) para alinhar o plot. */
  hideCategoryAxisLabels?: boolean
}

export function mergeCorrelatedChartOptions<T extends Record<string, unknown>>(
  options: T,
  params: ICorrelatedChartLayoutParams,
): T {
  const grid = (options.grid ?? {}) as Record<string, unknown>
  const yaxis = (options.yaxis ?? {}) as Record<string, unknown>
  const yLabels = (yaxis.labels ?? {}) as Record<string, unknown>
  const hideDoorAxis = Boolean(params.hideCategoryAxisLabels)

  const alignedYaxis = hideDoorAxis
    ? {
        ...yaxis,
        show: false,
        floating: true,
        labels: {
          ...yLabels,
          show: false,
        },
      }
    : {
        ...yaxis,
        labels: {
          ...yLabels,
          minWidth: CORRELATED_Y_AXIS_LABEL_WIDTH,
          maxWidth: CORRELATED_Y_AXIS_LABEL_WIDTH,
          trim: true,
        },
      }

  return {
    ...options,
    grid: {
      ...grid,
      padding: {
        ...CORRELATED_CHART_GRID_PADDING,
        left: hideDoorAxis
          ? CORRELATED_DOOR_PLOT_LEFT_PADDING
          : CORRELATED_CHART_GRID_PADDING.left,
      },
    },
    yaxis: alignedYaxis,
    xaxis: buildCorrelatedXAxisOptions({
      min: params.xAxisMin,
      max: params.xAxisMax,
      showLabels: params.showXAxisLabels,
    }),
  }
}
